import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, ExternalLink } from "lucide-react";
import { TT_LINKS } from "../data/timbertech";

const EASE = [0.22, 1, 0.36, 1];
const DISCLAIMER =
  "Colors shown on screen are approximate representations and may vary from actual decking. We recommend viewing physical samples before making a final selection.";

/** Persist the homeowner's product interest for the estimate form (no account needed). */
function rememberInterest(text) {
  try {
    sessionStorage.setItem("tt_interest", text);
  } catch (_) {}
}

/**
 * Board-style fallback swatch — approximates the color with a subtle
 * grain effect. Used until official TimberTech imagery is approved;
 * any color can carry an `image` URL in the catalog to upgrade it.
 * Legacy string colors from older CMS saves are normalized to a neutral swatch.
 */
function normalizeColor(color) {
  if (typeof color === "string") {
    return { name: color, hex: "#B9AE9C", hex2: "#A39885" };
  }
  return color;
}

function BoardSwatch({ color, className = "" }) {
  color = normalizeColor(color);
  if (color.image) {
    return (
      <img
        src={color.image}
        alt={`TimberTech ${color.collectionName || ""} ${color.name} decking color`.trim()}
        loading="lazy"
        decoding="async"
        className={`object-cover ${className}`}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`block ${className}`}
      style={{
        background: `linear-gradient(155deg, ${color.hex} 0%, ${color.hex2} 100%)`,
        backgroundImage: `linear-gradient(155deg, ${color.hex} 0%, ${color.hex2} 100%), repeating-linear-gradient(92deg, rgba(255,255,255,0.05) 0 1px, transparent 1px 5px), repeating-linear-gradient(88deg, rgba(0,0,0,0.05) 0 1px, transparent 1px 9px)`,
        backgroundBlendMode: "normal, overlay, overlay",
      }}
    />
  );
}

export default function TimberTechExplorer({ open, mode, onClose, composite, pvc, railing }) {
  const [detail, setDetail] = useState(null); // { color(obj), collection, collectionUrl, groupLabel }
  const nav = useNavigate();

  const close = useCallback(() => {
    setDetail(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, close]);

  useEffect(() => {
    if (open) setDetail(null);
  }, [open, mode]);

  if (!open) return null;

  const isRailing = mode === "railing";
  const groupLabel = mode === "composite" ? "TimberTech Composite" : mode === "pvc" ? "TimberTech Advanced PVC" : "TimberTech Railing";
  const heading =
    mode === "composite"
      ? "Explore TimberTech Composite"
      : mode === "pvc"
        ? "Explore TimberTech Advanced PVC"
        : "Explore TimberTech Railing";
  const subheading =
    mode === "composite"
      ? "Real wood looks. Low-maintenance performance. Explore colors and collections for your new deck."
      : mode === "pvc"
        ? "Premium synthetic decking with realistic wood looks and exceptional moisture resistance."
        : "Complete your outdoor space with railing systems designed to complement TimberTech decking.";
  const collections = mode === "composite" ? composite : pvc;

  const goEstimate = () => {
    if (detail) {
      rememberInterest(`Interested in: ${detail.groupLabel} — ${detail.collection} — ${detail.color.name}`);
    } else if (isRailing) {
      rememberInterest("Interested in: TimberTech Railing options");
    } else {
      rememberInterest(`Interested in: ${groupLabel}`);
    }
    close();
    nav("/contact");
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        data-testid="tt-explorer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm"
        onClick={close}
      >
        <motion.div
          initial={{ y: 48, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 32, opacity: 0 }}
          transition={{ duration: 0.45, ease: EASE }}
          className="absolute inset-x-0 bottom-0 top-[4vh] md:inset-x-[4vw] md:top-[4vh] md:bottom-[4vh] bg-[#FBF8F3] md:rounded-sm overflow-hidden flex flex-col shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={heading}
        >
          {/* Header */}
          <div className="shrink-0 px-6 md:px-12 pt-8 md:pt-10 pb-6 border-b border-[#E7E2D9] bg-[#FBF8F3]">
            <div className="flex items-start justify-between gap-6">
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#99582A] font-semibold">
                  {detail ? `${detail.groupLabel} · ${detail.collection}` : groupLabel}
                </p>
                <h2 data-testid="tt-explorer-heading" className="font-display text-3xl md:text-5xl tracking-tighter font-medium mt-2 text-[#111]">
                  {detail ? detail.color.name : heading}
                </h2>
                {!detail && (
                  <p className="mt-3 text-sm md:text-base text-[#4A4A4A] max-w-2xl leading-relaxed">{subheading}</p>
                )}
              </div>
              <button
                onClick={close}
                data-testid="tt-explorer-close"
                aria-label="Close"
                className="shrink-0 w-11 h-11 border border-[#E7E2D9] hover:border-[#99582A] hover:text-[#99582A] flex items-center justify-center transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 md:px-12 py-8 md:py-10">
            {detail ? (
              /* ---- Per-color detail view ---- */
              <div data-testid="tt-color-detail" className="max-w-4xl mx-auto py-4 md:py-8">
                <button
                  onClick={() => setDetail(null)}
                  data-testid="tt-detail-back"
                  className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#99582A] mb-8 transition-colors"
                >
                  <ArrowLeft size={15} /> Back to all colors
                </button>
                <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                  <BoardSwatch
                    color={{ ...detail.color, collectionName: detail.collection }}
                    className="w-full aspect-[4/5] max-h-[420px] border border-black/10 shadow-[0_24px_50px_-24px_rgba(17,17,17,0.35)]"
                  />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.3em] text-[#99582A] font-semibold">
                      {detail.groupLabel}
                    </p>
                    <p className="font-display text-4xl md:text-5xl tracking-tighter font-medium mt-3 text-[#111]">
                      {detail.color.name}
                    </p>
                    <p className="mt-2 text-sm uppercase tracking-[0.25em] text-[#666] font-semibold">
                      {detail.collection}
                    </p>
                    <p className="mt-6 text-[#4A4A4A] leading-relaxed">
                      Like this look? We can bring physical samples of this color — and nearby
                      options in the same collection — to your free on-site estimate so you can
                      compare them against your home.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={goEstimate}
                        data-testid="tt-detail-estimate-btn"
                        className="group shrink-0 whitespace-nowrap bg-[#111] text-white hover:bg-[#99582A] transition-colors px-7 py-4 font-semibold tracking-wide inline-flex items-center justify-center gap-2.5"
                      >
                        Request a Free Estimate <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </button>
                      <a
                        href={detail.color.url || detail.collectionUrl || TT_LINKS.samples}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="tt-detail-samples-btn"
                        className="shrink-0 whitespace-nowrap border-2 border-[#111] hover:bg-[#111] hover:text-white transition-colors px-7 py-4 font-semibold tracking-wide inline-flex items-center justify-center gap-2.5"
                      >
                        View TimberTech Samples <ExternalLink size={15} />
                      </a>
                    </div>
                    <p className="mt-4 text-xs text-[#666]">
                      Samples link opens the official TimberTech {detail.collection} page.
                    </p>
                  </div>
                </div>
              </div>
            ) : isRailing ? (
              /* ---- Railing explorer ---- */
              <div className="space-y-8">
                {railing.map((fam) => (
                  <section key={fam.family} data-testid={`tt-rail-${fam.family.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="border border-[#E7E2D9] bg-white p-6 md:p-10">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-[#99582A] font-semibold">{fam.kind}</p>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium mt-2 text-[#111]">{fam.family}</h3>
                    {fam.topRails.length > 0 ? (
                      <div className="mt-6 grid sm:grid-cols-3 gap-6">
                        <RailGroup label="Top rail styles" items={fam.topRails} testPrefix={`${fam.family}-toprails`} />
                        <RailGroup label="Infill options" items={fam.infills} testPrefix={`${fam.family}-infills`} />
                        <RailGroup label="Colors & finishes" items={fam.colors} testPrefix={`${fam.family}-colors`} />
                      </div>
                    ) : null}
                    <p className="mt-6 text-xs text-[#666] italic">{fam.note}</p>
                  </section>
                ))}
              </div>
            ) : (
              /* ---- Decking color explorer ---- */
              <div className="space-y-12">
                {collections.map((col) => (
                  <section key={col.collection}>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium text-[#111] border-b border-[#E7E2D9] pb-4">
                      {col.collection}
                    </h3>
                    <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {col.colors.map(normalizeColor).map((color) => (
                        <button
                          key={color.name}
                          onClick={() => setDetail({ color, collection: col.collection, collectionUrl: col.url, groupLabel })}
                          data-testid={`tt-color-${color.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          aria-label={`TimberTech ${col.collection} ${color.name} decking color`}
                          className="group text-left border border-[#E7E2D9] bg-white hover:border-[#99582A] hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(17,17,17,0.25)] transition-all duration-300 overflow-hidden"
                        >
                          <BoardSwatch color={color} className="w-full aspect-[4/3]" />
                          <span className="block p-4">
                            <span className="block font-display text-base md:text-lg tracking-tight font-medium text-[#111] leading-snug">
                              {color.name}
                            </span>
                            <span className="block mt-1 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#666]">
                              {col.collection}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-[#E7E2D9] bg-white px-6 md:px-12 py-5 md:py-6">
            {isRailing ? (
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <p className="text-sm text-[#4A4A4A] max-w-xl">
                  <b className="text-[#111]">Not sure which railing works with your deck?</b>{" "}
                  We'll help you compare railing styles during your free on-site estimate.
                </p>
                <button
                  onClick={goEstimate}
                  data-testid="tt-explorer-estimate-btn"
                  className="group shrink-0 bg-[#111] text-white hover:bg-[#99582A] transition-colors px-7 py-3.5 font-semibold tracking-wide inline-flex items-center gap-3"
                >
                  Request a Free Estimate <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <p className="text-xs text-[#666] max-w-xl leading-relaxed">{DISCLAIMER}</p>
                <div className="flex flex-wrap gap-3 shrink-0">
                  <button
                    onClick={goEstimate}
                    data-testid="tt-explorer-estimate-btn"
                    className="group bg-[#111] text-white hover:bg-[#99582A] transition-colors px-6 py-3.5 font-semibold tracking-wide inline-flex items-center gap-2.5"
                  >
                    Request a Free Estimate <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                  <a
                    href={detail?.color?.url || detail?.collectionUrl || TT_LINKS.samples}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="tt-explorer-samples-btn"
                    className="border-2 border-[#111] hover:bg-[#111] hover:text-white transition-colors px-6 py-3.5 font-semibold tracking-wide inline-flex items-center gap-2.5"
                  >
                    View TimberTech Samples <ExternalLink size={15} />
                  </a>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

function RailGroup({ label, items, testPrefix }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#99582A]">{label}</p>
      <ul className="mt-3 space-y-2">
        {items.map((it) => (
          <li key={it} data-testid={`${testPrefix}-${it.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-sm text-[#111] flex items-start gap-2.5">
            <span className="mt-[7px] w-4 h-px bg-[#C27A3E] shrink-0" />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
